import { useState, type FormEvent } from "react";

const footerLinks = [
  { label: "热门连载", href: "#serializing" },
  { label: "今日更新", href: "#updates" },
  { label: "完结漫画", href: "#completed" },
  { label: "追更指南", href: "#guide" },
  { label: "常见问题", href: "#faq" },
  { label: "关于我们", href: "#about" },
];

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot check
    const honeypot = String(fd.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");
    try {
      const res = await fetch("https://readdy.ai/api/form/da20qqlv7gurs51ef2n0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (res.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg ? String(serverMsg) : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <footer className="bg-foreground-900 text-background-200">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr_1.4fr]">
          <div>
            <a href="#top" className="flex items-center gap-2 font-heading text-xl text-background-50" title="吹雪漫画网">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-snowflake-line text-lg"></i>
              </span>
              吹雪漫画网
            </a>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-background-300">
              专注连载中与完结漫画的追更进度与最新话更新提醒，让每一话都清楚、追更不迷路。
            </p>
            <div className="mt-6 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100/15 text-background-100">
                <i className="ri-mail-line"></i>
              </span>
              <a href="mailto:hello@chuixuecomic.com" className="text-sm text-background-200 transition hover:text-accent-400">
                hello@chuixuecomic.com
              </a>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-semibold text-background-50">站内导航</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3">
              {footerLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="whitespace-nowrap text-sm text-background-300 transition hover:text-accent-400"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-semibold text-background-50">意见反馈</h3>
            <p className="mb-4 text-xs text-background-400">
              发现信息有误或想推荐好看的漫画？欢迎告诉我们，我们会第一时间跟进。
            </p>
            <form
              id="feedback-form"
              data-readdy-form="吹雪漫画网 意见反馈表单"
              onSubmit={handleSubmit}
              className="space-y-3"
              noValidate
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  type="text"
                  name="name"
                  placeholder="你的称呼"
                  maxLength={50}
                  className="w-full rounded-md bg-background-100/10 px-3 py-2.5 text-sm text-background-50 outline-none ring-1 ring-background-200/30 placeholder:text-background-400 focus:ring-primary-400"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="联系邮箱（选填）"
                  maxLength={100}
                  className="w-full rounded-md bg-background-100/10 px-3 py-2.5 text-sm text-background-50 outline-none ring-1 ring-background-200/30 placeholder:text-background-400 focus:ring-primary-400"
                />
              </div>
              <textarea
                name="message"
                placeholder="想反馈的内容（最多 500 字）"
                required
                maxLength={500}
                rows={4}
                className="w-full resize-none rounded-md bg-background-100/10 px-3 py-2.5 text-sm text-background-50 outline-none ring-1 ring-background-200/30 placeholder:text-background-400 focus:ring-primary-400"
              ></textarea>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
              {status === "success" && (
                <p className="rounded-md bg-primary-100/15 px-3 py-2 text-xs text-primary-200">
                  已收到你的反馈，感谢支持！我们会尽快处理。
                </p>
              )}
              {status === "error" && formError && (
                <p className="rounded-md bg-primary-500/20 px-3 py-2 text-xs text-primary-100">
                  提交失败：{formError}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-background-200/20 pt-6 sm:flex-row">
          <p className="text-xs text-background-400">
            © 2026 吹雪漫画网 · 追更进度与最新话更新提醒
          </p>
          <div className="flex items-center gap-4 text-xs text-background-400">
            <a href="#top" className="transition hover:text-accent-400">回到顶部</a>
            <a href="#about" className="transition hover:text-accent-400">关于我们</a>
          </div>
        </div>
      </div>
    </footer>
  );
}